import { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination, SelectPicker, Table, Tooltip, Whisper } from "rsuite";
import { Button } from "../../components/button.component";
import { PaginationEntriesInput } from "../../components/paginationEntries";
import { AppRouteApi } from "../../config/appRoute";
import { useAppDispatch, useAppSelector } from "../../core/redux";
import { ActionsPatientTable } from "../../core/redux/slices/slicePatientTable";
import {
  IPaginationSearchFilter,
  useTablePaginationSearchFilter,
} from "../../hooks";
import {
  ApiSchemaPatient,
  ApiSchemaFilteredPatient,
} from "../../models/api/patient";
import { AxiosAuth } from "../../core/axios";
import { FcOk, FcHighPriority } from "react-icons/fc";
import { FaUser, FaIdCard, FaExchangeAlt } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { ApiPatient } from "../../core/apis";
import { StorageAuth } from "../../core/localStorage";
import { showToast } from "../../components/toast.component";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaPatient & {
  name: string;
};

export function PatientDataExchangeTable({
  selectedCompany,
}: {
  selectedCompany: string;
}) {
  const dispatch = useAppDispatch();
  const { pageNumber, pageSize, nextPageToken, shouldRefetch, searchKey } =
    useAppSelector((state) => state.patientTable);
  const { data: allPatientData, isValidating: isLoadingPatientDropdown } =
    ApiPatient.GetAllFromCompany.Hook(selectedCompany);
  const [insuranceId, setInsuranceId] = useState("");
  const apiParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      SearchKey: searchKey,
      PageNumber: pageNumber,
      PageSize: pageSize,
      NextPageToken: nextPageToken,
      CompanyId: selectedCompany,
      InsuranceTypeId: insuranceId,
    };
  }, [
    searchKey,
    pageNumber,
    pageSize,
    nextPageToken,
    selectedCompany,
    insuranceId,
  ]);
  const { apiData, isFetching, refetch } = useTablePaginationSearchFilter<
    ApiSchemaFilteredPatient,
    typeof ActionsPatientTable
  >(ActionsPatientTable, {
    customParams: apiParams,
    apiRoute: AppRouteApi.Patient.DataExchange(),
  });
  const { data: insuranceTypes } = ApiPatient.GetAllInsuranceTypes.Hook(
    StorageAuth.CompanyId!
  );
  const [isHHASyncing, setIsHHASyncing] = useState({ status: false, id: "" });
  const insuranceTypeMap =
    insuranceTypes?.map((it) => ({
      label: it.displayName ?? it.name,
      value: it.sKey,
    })) ?? [];
  const allPatientMap =
    allPatientData?.map((patient) => ({
      label: `${patient.firstName} ${patient.lastName}`,
      value: `${patient.firstName}`,
    })) ?? [];

  const disabledInsuranceType = () => {
    const disabledIT: string[] = [];
    insuranceTypes?.forEach((it, idx) => {
      if (it.isDisabled) disabledIT.push(insuranceTypeMap[idx].value!);
    });
    return disabledIT;
  };

  const handleHHASync = async (id: string) => {
    try {
      setIsHHASyncing({ status: true, id: id });
      await AxiosAuth.get(
        AppRouteApi.HouseKeeping.UpdatePatientHHAIdByPatientIdAsync(id)
      );
      showToast("HHA Exchange ID updated successfully", "success", "topEnd");
      refetch();
    } catch (error: any) {
      const parsedData = JSON.parse(error.message).data;
      const errorMessage =
        parsedData.message || "Failed to update HHA Exchange ID";
      // Check if the error message contains specific patterns and provide a more user-friendly message
      if (errorMessage.includes("PatientID element not found in response")) {
        // Extract insurance ID if present in the error message
        const insuranceIdMatch = parsedData.message.match(
          /Patient InsuranceId: (\d+)/
        );
        const insuranceId = insuranceIdMatch ? insuranceIdMatch[1] : "unknown";

        // Create a more user-friendly error message
        const friendlyMessage = `No patient found in HHA Exchange with the provided Insurance ID (${insuranceId}). 
          Please verify the Insurance ID is correct or contact HHA Exchange support.`;

        showToast(friendlyMessage, "error", "topEnd", 10000);
      } else {
        // Show the original error message if it doesn't match our known patterns
        showToast(errorMessage, "error", "topEnd", 10000);
      }
      // console.error(error);
    } finally {
      setIsHHASyncing({ status: false, id: "" });
    }
  };

  const onPageChange = useCallback(
    (value) => dispatch(ActionsPatientTable.setEntryCount(value)),
    [dispatch]
  );

  const handleInsuranceTypeChange = (value: string) => {
    setInsuranceId(value);
    dispatch(ActionsPatientTable.goToPage(1));
  };
  const handlePatientChange = (value: string) => {
    dispatch(ActionsPatientTable.setSearchKey(value));
    dispatch(ActionsPatientTable.goToPage(1));
  };

  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  useEffect(() => {
    return () => {
      dispatch(ActionsPatientTable.resetData());
    };
  }, [dispatch]);

  const [sortBy, setSortBy] = useState<keyof TableData>("name");
  const [sortAsc, setSortAsc] = useState(true);

  const tableData = useMemo(() => {
    return (apiData?.list || []).map((patient) => ({
      ...patient,
      name: `${patient.firstName} ${patient.lastName}`,
    }));
  }, [apiData?.list]);

  const sortedData = useMemo(() => {
    if (!sortBy) return tableData;
    return [...tableData].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      const compareResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortAsc ? compareResult : -compareResult;
    });
  }, [tableData, sortBy, sortAsc]);

  return (
    <div className="py-1">
      <div className="row g-0 mb-4">
        <div className="col-12 mb-3">
          <div className="d-flex flex-wrap gap-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">Filters:</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span>Insurance Type</span>
              <div style={{ minWidth: "200px" }}>
                <SelectPicker
                  onChange={(value) =>
                    handleInsuranceTypeChange(value as string)
                  }
                  data={insuranceTypeMap}
                  disabledItemValues={disabledInsuranceType()}
                  placeholder="Select Insurance Type"
                  cleanable
                  block
                />
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span>Patient</span>
              <div style={{ minWidth: "200px" }}>
                <SelectPicker
                  onChange={(value) => handlePatientChange(value as string)}
                  data={allPatientMap}
                  placeholder="Select Patient"
                  cleanable
                  block
                  disabled={isLoadingPatientDropdown}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Table
        autoHeight
        data={sortedData || []}
        loading={isFetching}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortColumn, sortType) => {
          setSortBy(sortColumn as keyof TableData);
          setSortAsc(sortType === "asc");
        }}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Patient Available</h3>
          </div>
        )}
      >
        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Patient Name">
              <FaUser className="me-2" size={14} /> Patient Name
            </span>
          </HeaderCell>
          <Cell dataKey="name" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Insurance ID">
              <FaIdCard className="me-2" size={14} /> Insurance ID
            </span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div className="d-flex align-items-center">
                {rowData.insuranceId ? (
                  <>
                    <FaIdCard className="me-2" size={14} color="#28a745" />
                    <span>{rowData.insuranceId}</span>
                  </>
                ) : (
                  <span className="text-muted">Not provided</span>
                )}
              </div>
            )}
          </Cell>
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Insurance ID">
              <FaIdCard className="me-2" size={14} /> HHA Exchange ID
            </span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div className="d-flex align-items-center">
                {rowData.hhaExchangeId ? (
                  <>
                    <FaIdCard className="me-2" size={14} color="#28a745" />
                    <span>{rowData.hhaExchangeId}</span>
                  </>
                ) : (
                  <span className="text-muted">Not provided</span>
                )}
              </div>
            )}
          </Cell>
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="HHA Exchange">
              <FaExchangeAlt className="me-2" size={14} /> HHA Exchange Ready
            </span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaPatient) =>
              rowData.hhaExchangeId ? (
                <div className="d-flex align-items-center">
                  <FcOk size={24} />
                  <span className="ms-2 text-success fw-bold">Ready</span>
                </div>
              ) : (
                <div className="d-flex align-items-center">
                  <FcHighPriority size={20} className="me-2" />
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={
                      <Tooltip>
                        Please go to patient profile and update insurance ID
                        number
                      </Tooltip>
                    }
                  >
                    <Button
                      name="Get Ready For HHA"
                      className="btn-secondary btn-sm"
                      onClick={() => handleHHASync(rowData.sKey)}
                      loading={
                        isHHASyncing.status && rowData.sKey === isHHASyncing.id
                      }
                    />
                  </Whisper>
                </div>
              )
            }
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Edit</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaPatient) => (
              <a
                href={`/patient/update/${rowData.sKey}`}
                className="btn btn-sm btn-outline-primary"
                title="Edit Patient"
              >
                <FiEdit />
              </a>
            )}
          </Cell>
        </Column>
      </Table>

      {apiData?.list && (
        <div className="d-flex justify-content-between mt-3">
          <div className="d-flex align-items-center">
            <span className="me-2">Show</span>
            <PaginationEntriesInput onChange={onPageChange} />
            <span className="ms-2">entries</span>
          </div>
          <Pagination
            total={apiData.list.length || 0}
            pages={apiData?.list ? Math.ceil(apiData.totalCount / pageSize) : 0}
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page: any) =>
              dispatch(ActionsPatientTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
}
